import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import api from '../../utils/api';
import { useAdminTheme } from './ui/useAdminTheme';
import styles from './AdminEnterprisePage.module.css';

const PLANS = ['none', 'starter', 'professional', 'enterprise', 'custom'];
const BILLING_CADENCE = ['none', 'monthly', 'yearly'];
const BILLING_STATUS = ['none', 'draft', 'active', 'past_due', 'paused', 'cancelled'];

const LEDGER_BADGE_CLASS = {
  ok: styles.badgeOk,
  warn: styles.badgeWarn,
  bad: styles.badgeBad,
  neutral: styles.badgeNeutral,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

function splitEmailPaste(text) {
  return String(text || '')
    .split(/[\s,;]+/u)
    .map((s) => s.trim().toLowerCase().replace(/^[,;]+|[,;]+$/g, ''))
    .filter(Boolean);
}

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

function shortId(id) {
  if (!id) return '';
  const s = String(id);
  return s.length <= 8 ? s : `…${s.slice(-6)}`;
}

function adminMeterPercent(row) {
  const max = Math.max(1, Number(row.maxEnterpriseAdmins) || 1);
  const n = Number(row.adminCount) || 0;
  return Math.min(100, Math.round((n / max) * 100));
}

function isAdminNearCap(row) {
  const max = Number(row.maxEnterpriseAdmins) || 5;
  const n = Number(row.adminCount) || 0;
  return n >= max;
}

function AdminEmailChipField({
  emails,
  onChange,
  disabled,
  inputId,
  hint,
  chipError,
  setChipError,
}) {
  const [draft, setDraft] = useState('');

  const addAddresses = (candidates) => {
    setChipError('');
    const next = [...emails];
    let invalid = false;
    for (const c of candidates) {
      const raw = c.trim().toLowerCase();
      if (!raw) continue;
      if (!EMAIL_RE.test(raw)) {
        invalid = true;
        continue;
      }
      if (next.includes(raw)) continue;
      next.push(raw);
    }
    if (invalid) setChipError('One or more pasted addresses were skipped (invalid format).');
    onChange(next);
  };

  const commit = () => {
    const raw = draft.trim().toLowerCase();
    setChipError('');
    if (!raw) return;
    if (!EMAIL_RE.test(raw)) {
      setChipError('Enter a valid email address');
      return;
    }
    if (emails.includes(raw)) {
      setDraft('');
      return;
    }
    onChange([...emails, raw]);
    setDraft('');
  };

  return (
    <div className={styles.chipField}>
      <p className={styles.chipHint}>{hint}</p>
      <div className={styles.chipBar} data-no-enterprise-deselect>
        {emails.map((em) => (
          <span key={em} className={styles.emailChip}>
            {em}
            <button
              type="button"
              className={styles.chipRemove}
              disabled={disabled}
              onClick={() => onChange(emails.filter((x) => x !== em))}
              aria-label={`Remove ${em}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={inputId}
          className={styles.chipInput}
          type="email"
          value={draft}
          disabled={disabled}
          placeholder={emails.length ? 'Add another…' : 'name@company.com'}
          onChange={(ev) => setDraft(ev.target.value)}
          onPaste={(ev) => {
            const text = ev.clipboardData?.getData('text') || '';
            if (!text.includes('@')) return;
            const parts = splitEmailPaste(text);
            if (parts.length < 2) return;
            ev.preventDefault();
            setDraft('');
            addAddresses(parts);
          }}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ',') {
              ev.preventDefault();
              commit();
            } else if (ev.key === 'Backspace' && !draft && emails.length) {
              onChange(emails.slice(0, -1));
            }
          }}
          onBlur={() => {
            const t = draft.trim();
            if (!t) return;
            if (t.includes('@') && /[,;\s]/.test(t)) {
              addAddresses(splitEmailPaste(t));
              setDraft('');
              return;
            }
            commit();
          }}
        />
      </div>
      {chipError ? <p className={styles.chipError}>{chipError}</p> : null}
    </div>
  );
}

export default function AdminEnterprisePage() {
  const { account, setRefreshHandler } = useOutletContext();
  const { theme } = useAdminTheme();
  const mainSurfaceRef = useRef(null);

  const [enterprises, setEnterprises] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [formMsg, setFormMsg] = useState('');
  const [formErr, setFormErr] = useState('');

  const [createChipError, setCreateChipError] = useState('');
  const [detailChipError, setDetailChipError] = useState('');
  const [provisionFromEnquiryId, setProvisionFromEnquiryId] = useState(null);

  const [createForm, setCreateForm] = useState({
    name: '',
    subscriptionPlan: 'none',
    maxEnterpriseAdmins: 5,
    maxEnterpriseUsersPerAdmin: 50,
    adminEmails: [],
  });

  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [enquiries, setEnquiries] = useState([]);
  const [enquiriesLoading, setEnquiriesLoading] = useState(false);
  const [enquiriesError, setEnquiriesError] = useState('');
  const [enquiriesTotal, setEnquiriesTotal] = useState(0);
  const [enquiriesPage, setEnquiriesPage] = useState(1);
  const [enquiriesHasMore, setEnquiriesHasMore] = useState(false);
  const [enquiriesLoadingMore, setEnquiriesLoadingMore] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError('');
    try {
      const res = await api.get('/api/admin/enterprise');
      setEnterprises(res.data.enterprises || []);
    } catch (e) {
      setListError(e.response?.data?.error || 'Failed to load companies');
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadEnquiries = useCallback(async (page = 1, append = false) => {
    if (append) setEnquiriesLoadingMore(true);
    else {
      setEnquiriesLoading(true);
      setEnquiriesPage(1);
    }
    setEnquiriesError('');
    try {
      const res = await api.get('/api/admin/enterprise/enquiries', {
        params: { limit: 25, page },
      });
      const list = res.data.enquiries || [];
      setEnquiries((prev) => {
        if (!append) return list;
        const ids = new Set(prev.map((e) => String(e._id)));
        const merged = [...prev];
        for (const e of list) {
          if (!ids.has(String(e._id))) merged.push(e);
        }
        return merged;
      });
      setEnquiriesTotal(res.data.total ?? list.length);
      setEnquiriesPage(page);
      setEnquiriesHasMore(!!res.data.hasMore);
    } catch (e) {
      setEnquiriesError(e.response?.data?.error || 'Failed to load enquiries');
    } finally {
      setEnquiriesLoading(false);
      setEnquiriesLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (account?.role === 'admin') {
      loadList();
      loadEnquiries();
    }
  }, [account, loadList, loadEnquiries]);

  useEffect(() => {
    setRefreshHandler(() => {
      loadList();
      loadEnquiries();
    });
  }, [setRefreshHandler, loadList, loadEnquiries]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    setDetailError('');
    setFormMsg('');
    setFormErr('');
    try {
      const res = await api.get(`/api/admin/enterprise/${id}`);
      setDetail(res.data);
      const e = res.data.enterprise;
      setEditForm({
        name: e.name,
        subscriptionPlan: e.subscriptionPlan || 'none',
        maxEnterpriseAdmins: e.maxEnterpriseAdmins ?? 5,
        maxEnterpriseUsersPerAdmin: e.maxEnterpriseUsersPerAdmin ?? 50,
        accountNotes: e.accountNotes || '',
        billingPlanName: e.billingPlanName || '',
        billingCadence: e.billingCadence || 'none',
        subscriptionAmount: e.subscriptionAmount != null ? String(e.subscriptionAmount) : '',
        setupFeeAmount: e.setupFeeAmount != null ? String(e.setupFeeAmount) : '',
        billingStatus: e.billingStatus || 'none',
        subscriptionStartDate: e.subscriptionStartDate ? e.subscriptionStartDate.slice(0, 10) : '',
        nextRenewalDate: e.nextRenewalDate ? e.nextRenewalDate.slice(0, 10) : '',
        detailAdminEmails: [],
      });
    } catch (e) {
      setDetailError(e.response?.data?.error || 'Failed to load company');
      setDetail(null);
      setEditForm(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else {
      setDetail(null);
      setEditForm(null);
    }
  }, [selectedId, loadDetail]);

  useEffect(() => {
    const onDocMouseDown = (ev) => {
      if (!selectedId) return;
      const t = ev.target;
      if (!(t instanceof Node)) return;
      if (t.closest?.('[data-no-enterprise-deselect]')) return;
      if (t.closest?.('[role="dialog"]')) return;
      if (t.closest?.('.enterprise-modal-overlay')) return;
      if (t.closest?.('[data-radix-popper-content-wrapper]')) return;
      if (t.closest?.('[data-radix-portal]')) return;
      if (t.closest?.('[popover]')) return;
      if (mainSurfaceRef.current?.contains(t)) return;
      setSelectedId(null);
      setFormMsg('');
      setFormErr('');
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [selectedId]);

  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key !== 'Escape') return;
      if (deleteModalOpen) {
        if (!deleteSubmitting) {
          setDeleteModalOpen(false);
          setDeleteConfirmInput('');
        }
        return;
      }
      if (!selectedId) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      setSelectedId(null);
      setFormMsg('');
      setFormErr('');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedId, deleteModalOpen, deleteSubmitting]);

  const totals = useMemo(() => {
    const n = enterprises.length;
    const users = enterprises.reduce((s, e) => s + (e.userCount || 0), 0);
    const admins = enterprises.reduce((s, e) => s + (e.adminCount || 0), 0);
    return { n, users, admins };
  }, [enterprises]);

  const billingAttentionCount = useMemo(
    () => enterprises.filter((e) => e.billingAttention).length,
    [enterprises]
  );

  const atAdminCapCount = useMemo(
    () => enterprises.filter((e) => isAdminNearCap(e)).length,
    [enterprises]
  );

  const planMixTop = useMemo(() => {
    const m = {};
    enterprises.forEach((e) => {
      const p = e.subscriptionPlan || 'none';
      m[p] = (m[p] || 0) + 1;
    });
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [enterprises]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormErr('');
    setFormMsg('');
    try {
      const res = await api.post('/api/admin/enterprise', {
        name: createForm.name.trim(),
        subscriptionPlan: createForm.subscriptionPlan,
        maxEnterpriseAdmins: Number(createForm.maxEnterpriseAdmins),
        maxEnterpriseUsersPerAdmin: Number(createForm.maxEnterpriseUsersPerAdmin),
        adminEmails: createForm.adminEmails,
        provisionedFromEnquiryId: provisionFromEnquiryId || undefined,
      });
      const warns = res.data.adminAssignmentWarnings;
      const linkWarn = res.data.enquiryLinkWarning;
      if (linkWarn) setFormErr(linkWarn);
      const msg =
        warns?.length > 0
          ? `Company created. Admin assignment notes: ${warns.join(' · ')}`
          : 'Company created.';
      setFormMsg(msg);
      setCreateForm((f) => ({
        ...f,
        name: '',
        adminEmails: [],
      }));
      setProvisionFromEnquiryId(null);
      await loadList();
      await loadEnquiries();
      if (res.data.enterprise?._id) {
        setSelectedId(res.data.enterprise._id);
      }
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    if (!selectedId || !editForm) return;
    setSaving(true);
    setFormErr('');
    setFormMsg('');
    try {
      await api.patch(`/api/admin/enterprise/${selectedId}`, {
        name: editForm.name.trim(),
        subscriptionPlan: editForm.subscriptionPlan,
        maxEnterpriseAdmins: Number(editForm.maxEnterpriseAdmins),
        maxEnterpriseUsersPerAdmin: Number(editForm.maxEnterpriseUsersPerAdmin),
        accountNotes: editForm.accountNotes,
        billingPlanName: editForm.billingPlanName,
        billingCadence: editForm.billingCadence,
        subscriptionAmount: editForm.subscriptionAmount === '' ? null : Number(editForm.subscriptionAmount),
        setupFeeAmount: editForm.setupFeeAmount === '' ? null : Number(editForm.setupFeeAmount),
        billingStatus: editForm.billingStatus,
        subscriptionStartDate: editForm.subscriptionStartDate || null,
        nextRenewalDate: editForm.nextRenewalDate || null,
      });
      setFormMsg('Company updated.');
      await loadList();
      await loadDetail(selectedId);
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteAdmins = async (e, emails) => {
    e.preventDefault();
    if (!selectedId || !emails.length) return false;
    setSaving(true);
    setFormErr('');
    setFormMsg('');
    try {
      const res = await api.post(`/api/admin/enterprise/${selectedId}/invite-admin`, {
        emails,
      });
      const errs = res.data.errors;
      if (errs?.length) {
        setFormMsg(`${res.data.message || 'Done'} — ${errs.join(' · ')}`);
      } else {
        setFormMsg(res.data.message || 'Enterprise admins updated.');
      }
      await loadList();
      await loadDetail(selectedId);
      return true;
    } catch (err) {
      setFormErr(err.response?.data?.error || err.response?.data?.message || 'Assignment failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const patchEnquiryStatus = async (id, status) => {
    try {
      await api.patch(`/api/admin/enterprise/enquiries/${id}`, { status });
      await loadEnquiries();
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Failed to update enquiry');
    }
  };

  const enterpriseLegalName = detail?.enterprise?.name ?? '';
  const deleteNameMatches =
    !!enterpriseLegalName &&
    deleteConfirmInput.trim() === String(enterpriseLegalName).trim();

  const openDeleteModal = () => {
    setDeleteConfirmInput('');
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeleteModalOpen(false);
    setDeleteConfirmInput('');
  };

  const executeDelete = async () => {
    if (!selectedId || !deleteNameMatches) return;
    setDeleteSubmitting(true);
    setFormErr('');
    try {
      await api.delete(`/api/admin/enterprise/${selectedId}`, {
        data: { confirmEnterpriseName: deleteConfirmInput.trim() },
      });
      setDeleteModalOpen(false);
      setDeleteConfirmInput('');
      setSelectedId(null);
      setDetail(null);
      setEditForm(null);
      setFormMsg('Enterprise account permanently removed.');
      await loadList();
      await loadEnquiries();
    } catch (err) {
      setFormErr(err.response?.data?.error || 'Delete failed');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const prefillProvisionFromEnquiry = (enq) => {
    const maxAdmins = Math.max(5, Number(enq.adminLicences) || 5);
    setCreateForm((f) => ({
      ...f,
      name: enq.companyName || f.name,
      maxEnterpriseAdmins: maxAdmins,
      adminEmails: EMAIL_RE.test((enq.contactEmail || '').trim())
        ? [String(enq.contactEmail).trim().toLowerCase()]
        : [],
    }));
    setProvisionFromEnquiryId(enq._id);
    setSelectedId(null);
    setFormMsg('');
    setFormErr('');
    document.getElementById('new-name')?.focus?.();
  };

  if (!account) return null;

  return (
    <div className={styles.page} ref={mainSurfaceRef} data-admin-enterprise-surface>
          <header className={styles.execBar}>
            <div className={styles.execBarText}>
              <p className={styles.eyebrow}>Platform oversight</p>
              <h1 className={styles.execTitle}>Enterprise account control</h1>
              <p className={styles.execSubtitle}>
                Provision companies, set commercial limits, track billing posture, and assign enterprise
                administrators. This surface governs the Velox enterprise layer for your organization.
              </p>
            </div>
            <div className={styles.execMeta}>
              <span className={styles.signedIn}>
                Signed in as <strong>{account.email}</strong>
              </span>
            </div>
          </header>

          <section className={styles.kpiSection} aria-label="Summary metrics">
            <div className={styles.kpiGrid}>
              <div className={styles.kpiCard}>
                <p className={styles.kpiLabel}>Companies</p>
                <p className={styles.kpiValue}>{listLoading ? '—' : totals.n}</p>
                <p className={styles.kpiHint}>Active enterprise accounts</p>
              </div>
              <div className={styles.kpiCard}>
                <p className={styles.kpiLabel}>Enterprise users</p>
                <p className={styles.kpiValue}>{listLoading ? '—' : totals.users}</p>
                <p className={styles.kpiHint}>Across all companies</p>
              </div>
              <div className={styles.kpiCard}>
                <p className={styles.kpiLabel}>Enterprise admins</p>
                <p className={styles.kpiValue}>{listLoading ? '—' : totals.admins}</p>
                <p className={styles.kpiHint}>Seats in use</p>
              </div>
              <div className={styles.kpiCard}>
                <p className={styles.kpiLabel}>Billing attention</p>
                <p className={styles.kpiValue}>{listLoading ? '—' : billingAttentionCount}</p>
                <p className={styles.kpiHint}>Past due or legacy overdue</p>
              </div>
              <div className={styles.kpiCard}>
                <p className={styles.kpiLabel}>At admin limit</p>
                <p className={styles.kpiValue}>{listLoading ? '—' : atAdminCapCount}</p>
                <p className={styles.kpiHint}>Companies at cap</p>
              </div>
            </div>
            {!listLoading && planMixTop.length > 0 ? (
              <div className={styles.kpiCard} style={{ marginTop: 14 }}>
                <p className={styles.kpiLabel}>Plan distribution</p>
                <ul className={styles.planMix}>
                  {planMixTop.map(([plan, count]) => (
                    <li key={plan}>
                      <span>{plan}</span>
                      <span>{count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          {listError ? <div className={styles.flashError}>{listError}</div> : null}

          <section className={styles.enquirySection} aria-labelledby="enquiries-heading">
            <div className={styles.sectionHeader}>
              <h2 id="enquiries-heading" className={styles.sectionTitle}>
                Enterprise enquiries
              </h2>
              <p className={styles.sectionLead}>
                Inbound requests from the public enterprise page. Review details and provision a company when
                ready; the contact email can be pre-filled as the first enterprise admin.
                {enquiriesTotal > 0 ? (
                  <span className={styles.enquiryCountMeta}> Showing {enquiries.length} of {enquiriesTotal}.</span>
                ) : null}
              </p>
            </div>
            {enquiriesError ? <div className={styles.flashError}>{enquiriesError}</div> : null}
            {enquiriesLoading ? (
              <p className={styles.meta}>Loading enquiries…</p>
            ) : enquiries.length === 0 ? (
              <div className={styles.enquiryEmpty}>
                <p>No enquiries stored yet. New submissions will appear here and are still emailed to your admin
                  inbox.</p>
              </div>
            ) : (
              <div className={styles.enquiryGrid}>
                {enquiries.map((enq) => (
                  <article key={enq._id} className={styles.enquiryCard}>
                    <div className={styles.enquiryCardHead}>
                      <span className={styles.enquiryCompany}>{enq.companyName}</span>
                      <span
                        className={
                          {
                            new: styles.enquiryStatusNew,
                            reviewed: styles.enquiryStatusReviewed,
                            provisioned: styles.enquiryStatusProvisioned,
                            archived: styles.enquiryStatusArchived,
                          }[enq.status] || styles.enquiryStatusNew
                        }
                      >
                        {enq.status}
                      </span>
                    </div>
                    <dl className={styles.enquiryDl}>
                      <div>
                        <dt>Contact</dt>
                        <dd>
                          {enq.contactName} ·{' '}
                          <a href={`mailto:${enq.contactEmail}`}>{enq.contactEmail}</a>
                        </dd>
                      </div>
                      <div>
                        <dt>Team size</dt>
                        <dd>{enq.teamSize} members</dd>
                      </div>
                      <div>
                        <dt>Admin licences</dt>
                        <dd>{enq.adminLicences}</dd>
                      </div>
                      <div>
                        <dt>Custom branded cards</dt>
                        <dd>
                          {enq.customBrandedCards === 'yes' ? 'Yes' : 'No'}
                          {enq.customBrandedCardsDetail
                            ? ` — ${enq.customBrandedCardsDetail}`
                            : ''}
                        </dd>
                      </div>
                      <div>
                        <dt>Tracking interests</dt>
                        <dd>{enq.trackingInterests || '—'}</dd>
                      </div>
                      <div>
                        <dt>Weekly insights</dt>
                        <dd>
                          {enq.weeklyInsights === 'yes' ? 'Yes' : 'No'}
                          {enq.weeklyInsightsDetail ? ` — ${enq.weeklyInsightsDetail}` : ''}
                        </dd>
                      </div>
                      {enq.comments ? (
                        <div>
                          <dt>Comments</dt>
                          <dd>{enq.comments}</dd>
                        </div>
                      ) : null}
                      <div>
                        <dt>Submitted</dt>
                        <dd>{formatDate(enq.createdAt)}</dd>
                      </div>
                    </dl>
                    <div className={styles.enquiryActions}>
                      <button
                        type="button"
                        className={styles.btn}
                        disabled={enq.status === 'provisioned'}
                        onClick={() => prefillProvisionFromEnquiry(enq)}
                      >
                        Provision company
                      </button>
                      <button
                        type="button"
                        className={styles.btnSecondary}
                        disabled={enq.status === 'reviewed' || enq.status === 'provisioned'}
                        onClick={() => patchEnquiryStatus(enq._id, 'reviewed')}
                      >
                        Mark reviewed
                      </button>
                      <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={() => patchEnquiryStatus(enq._id, 'archived')}
                      >
                        Archive
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
            {!enquiriesLoading && enquiriesHasMore ? (
              <div className={styles.enquiryLoadMore}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  disabled={enquiriesLoadingMore}
                  onClick={() => loadEnquiries(enquiriesPage + 1, true)}
                >
                  {enquiriesLoadingMore ? 'Loading…' : 'Load more enquiries'}
                </button>
              </div>
            ) : null}
          </section>

          <div className={styles.mainGrid}>
            <section className={styles.ledger} aria-labelledby="ledger-heading">
              <div className={styles.sectionHeader}>
                <h2 id="ledger-heading" className={styles.sectionTitle}>
                  Accounts ledger
                </h2>
                <p className={styles.sectionLead}>
                  Select a row to review limits, billing, and enterprise administrators. Status reflects
                  subscription and billing posture at a glance.
                </p>
              </div>

              <div className={styles.tableShell}>
                {listLoading ? (
                  <div className={styles.skeletonTable} aria-hidden="true">
                    <div className={styles.skeletonRow} />
                    <div className={styles.skeletonRow} />
                    <div className={styles.skeletonRow} />
                    <div className={styles.skeletonRow} />
                  </div>
                ) : enterprises.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p className={styles.emptyTitle}>No enterprise companies yet</p>
                    <p className={styles.emptyLead}>
                      Use <strong>Provision company</strong> to create the first account. You can then assign
                      enterprise admins and set commercial limits.
                    </p>
                  </div>
                ) : (
                  <div className={styles.tableScroll}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Plan</th>
                          <th>Billing</th>
                          <th>Admins</th>
                          <th>Users</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enterprises.map((row) => {
                          const pct = adminMeterPercent(row);
                          const nearCap = isAdminNearCap(row);
                          const badge = row.ledgerBilling || { tone: 'neutral', label: '—' };
                          const badgeClass = LEDGER_BADGE_CLASS[badge.tone] || styles.badgeNeutral;
                          return (
                            <tr
                              key={row._id}
                              className={selectedId === row._id ? styles.rowSelected : ''}
                              onClick={() => {
                                setSelectedId(row._id);
                                setFormMsg('');
                                setFormErr('');
                              }}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') {
                                  ev.preventDefault();
                                  setSelectedId(row._id);
                                  setFormMsg('');
                                  setFormErr('');
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              aria-pressed={selectedId === row._id}
                            >
                              <td>
                                <div className={styles.companyCell}>
                                  <span className={styles.companyName}>{row.name}</span>
                                  <span className={styles.companyId}>{shortId(row._id)}</span>
                                </div>
                              </td>
                              <td>
                                <span className={styles.chipPlan}>{row.subscriptionPlan || 'none'}</span>
                              </td>
                              <td>
                                <span className={badgeClass}>{badge.label}</span>
                              </td>
                              <td>
                                <div className={styles.usageCell}>
                                  <span className={nearCap ? styles.usageWarn : undefined}>
                                    {row.adminCount}/{row.maxEnterpriseAdmins}
                                  </span>
                                  <div className={styles.meter} aria-hidden="true">
                                    <div
                                      className={nearCap ? styles.meterFillWarn : styles.meterFill}
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className={styles.usageCell}>{row.userCount}</span>
                                <span className={styles.usageMuted}> active</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>

            <aside className={styles.rail}>
              <div className={styles.railCard}>
                <h3 className={styles.railTitle}>Provision company</h3>
                <p className={styles.railDesc}>
                  Creates a new enterprise account. Add one or more enterprise admin emails before creating —
                  existing users are granted access immediately; new users receive a secure email invitation.
                </p>
                {provisionFromEnquiryId ? (
                  <p className={styles.provisionBanner}>
                    Provisioning from enquiry — company will be linked when you create.
                  </p>
                ) : null}
                {!selectedId && formMsg ? <p className={styles.success}>{formMsg}</p> : null}
                {!selectedId && formErr ? <p className={styles.error}>{formErr}</p> : null}
                <form onSubmit={handleCreate} data-no-enterprise-deselect>
                  <div className={styles.field}>
                    <label htmlFor="new-name">Company name</label>
                    <input
                      id="new-name"
                      value={createForm.name}
                      onChange={(ev) => setCreateForm((f) => ({ ...f, name: ev.target.value }))}
                      required
                      placeholder="Acme Ltd"
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="new-admins">Enterprise admins</label>
                    <AdminEmailChipField
                      inputId="new-admins"
                      emails={createForm.adminEmails}
                      onChange={(next) => setCreateForm((f) => ({ ...f, adminEmails: next }))}
                      disabled={saving}
                      hint="Add emails for people who should administer this company on Velox. Press Enter to add each address."
                      chipError={createChipError}
                      setChipError={setCreateChipError}
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="new-plan">Subscription plan</label>
                    <select
                      id="new-plan"
                      value={createForm.subscriptionPlan}
                      onChange={(ev) => setCreateForm((f) => ({ ...f, subscriptionPlan: ev.target.value }))}
                    >
                      {PLANS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="new-max-admins">Max enterprise admins</label>
                    <input
                      id="new-max-admins"
                      type="number"
                      min={1}
                      value={createForm.maxEnterpriseAdmins}
                      onChange={(ev) => setCreateForm((f) => ({ ...f, maxEnterpriseAdmins: ev.target.value }))}
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="new-max-users">Max users per admin</label>
                    <input
                      id="new-max-users"
                      type="number"
                      min={1}
                      value={createForm.maxEnterpriseUsersPerAdmin}
                      onChange={(ev) =>
                        setCreateForm((f) => ({ ...f, maxEnterpriseUsersPerAdmin: ev.target.value }))
                      }
                    />
                  </div>
                  <div className={styles.actions}>
                    <button type="submit" className={styles.btn} disabled={saving}>
                      {saving ? 'Creating…' : 'Create company'}
                    </button>
                    {provisionFromEnquiryId ? (
                      <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={() => setProvisionFromEnquiryId(null)}
                      >
                        Clear enquiry link
                      </button>
                    ) : null}
                  </div>
                </form>
              </div>

              {selectedId ? (
                <div className={styles.railCard}>
                  <h3 className={styles.railTitle}>Account detail</h3>
                  <p className={styles.railDesc}>
                    Billing and limits apply to this company only. Changes are effective immediately for new
                    invites.
                  </p>
                  <div className={styles.actions} style={{ marginTop: 0, marginBottom: 12 }}>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => {
                        setSelectedId(null);
                        setFormMsg('');
                        setFormErr('');
                      }}
                    >
                      Clear selection
                    </button>
                  </div>

                  {detailLoading ? (
                    <p className={styles.meta}>Loading account…</p>
                  ) : detailError ? (
                    <p className={styles.error}>{detailError}</p>
                  ) : detail && editForm ? (
                    <>
                      {formMsg ? <p className={styles.success}>{formMsg}</p> : null}
                      {formErr ? <p className={styles.error}>{formErr}</p> : null}

                      <div className={styles.statStrip}>
                        <span>
                          Teams: <strong>{detail.teamCount ?? '—'}</strong>
                        </span>
                        <span>
                          Enterprise users: <strong>{detail.totalEnterpriseUsers ?? '—'}</strong>
                        </span>
                        <span>
                          Admin slots:{' '}
                          <strong>
                            {detail.adminSlotsUsed ?? '—'} / {editForm.maxEnterpriseAdmins}
                          </strong>
                        </span>
                        {detail.enterprise?.billingAttention ? (
                          <span className={styles.badgeWarn}>Billing attention</span>
                        ) : null}
                      </div>

                      <hr className={styles.divider} />

                      <h4 className={styles.subsectionTitle}>Company & limits</h4>
                      <form onSubmit={handleSaveCompany} data-no-enterprise-deselect>
                        <div className={styles.field}>
                          <label htmlFor="edit-name">Name</label>
                          <input
                            id="edit-name"
                            value={editForm.name}
                            onChange={(ev) => setEditForm((f) => ({ ...f, name: ev.target.value }))}
                            required
                          />
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-plan">Plan</label>
                          <select
                            id="edit-plan"
                            value={editForm.subscriptionPlan}
                            onChange={(ev) => setEditForm((f) => ({ ...f, subscriptionPlan: ev.target.value }))}
                          >
                            {PLANS.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-max-admins">Max enterprise admins</label>
                          <input
                            id="edit-max-admins"
                            type="number"
                            min={1}
                            value={editForm.maxEnterpriseAdmins}
                            onChange={(ev) => setEditForm((f) => ({ ...f, maxEnterpriseAdmins: ev.target.value }))}
                          />
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-max-users">Max users per admin</label>
                          <input
                            id="edit-max-users"
                            type="number"
                            min={1}
                            value={editForm.maxEnterpriseUsersPerAdmin}
                            onChange={(ev) =>
                              setEditForm((f) => ({ ...f, maxEnterpriseUsersPerAdmin: ev.target.value }))
                            }
                          />
                        </div>

                        <hr className={styles.divider} />

                        <h4 className={styles.subsectionTitle}>Billing</h4>
                        <div className={styles.field}>
                          <label htmlFor="edit-billing-plan-name">Plan name (commercial label)</label>
                          <input
                            id="edit-billing-plan-name"
                            value={editForm.billingPlanName}
                            onChange={(ev) => setEditForm((f) => ({ ...f, billingPlanName: ev.target.value }))}
                            placeholder="e.g. Velox Enterprise — 50 seats"
                          />
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-billing-cadence">Billing cadence</label>
                          <select
                            id="edit-billing-cadence"
                            value={editForm.billingCadence}
                            onChange={(ev) => setEditForm((f) => ({ ...f, billingCadence: ev.target.value }))}
                          >
                            {BILLING_CADENCE.map((c) => (
                              <option key={c} value={c}>
                                {c === 'none' ? 'Not set' : c}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-sub-amount">Recurring subscription amount</label>
                          <input
                            id="edit-sub-amount"
                            type="number"
                            min={0}
                            step="0.01"
                            value={editForm.subscriptionAmount}
                            onChange={(ev) =>
                              setEditForm((f) => ({ ...f, subscriptionAmount: ev.target.value }))
                            }
                            placeholder="Major currency units (internal record)"
                          />
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-setup-fee">One-off setup fee</label>
                          <input
                            id="edit-setup-fee"
                            type="number"
                            min={0}
                            step="0.01"
                            value={editForm.setupFeeAmount}
                            onChange={(ev) => setEditForm((f) => ({ ...f, setupFeeAmount: ev.target.value }))}
                            placeholder="Optional"
                          />
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-billing-status">Billing status</label>
                          <select
                            id="edit-billing-status"
                            value={editForm.billingStatus}
                            onChange={(ev) => setEditForm((f) => ({ ...f, billingStatus: ev.target.value }))}
                          >
                            {BILLING_STATUS.map((s) => (
                              <option key={s} value={s}>
                                {s.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-sub-start">Subscription start</label>
                          <input
                            id="edit-sub-start"
                            type="date"
                            value={editForm.subscriptionStartDate}
                            onChange={(ev) =>
                              setEditForm((f) => ({ ...f, subscriptionStartDate: ev.target.value }))
                            }
                          />
                        </div>
                        <div className={styles.field}>
                          <label htmlFor="edit-next-renewal">Next renewal</label>
                          <input
                            id="edit-next-renewal"
                            type="date"
                            value={editForm.nextRenewalDate}
                            onChange={(ev) =>
                              setEditForm((f) => ({ ...f, nextRenewalDate: ev.target.value }))
                            }
                          />
                        </div>

                        <hr className={styles.divider} />

                        <h4 className={styles.subsectionTitle}>Internal notes</h4>
                        <div className={styles.field}>
                          <label htmlFor="edit-notes">Notes</label>
                          <textarea
                            id="edit-notes"
                            value={editForm.accountNotes}
                            onChange={(ev) => setEditForm((f) => ({ ...f, accountNotes: ev.target.value }))}
                          />
                        </div>
                        <div className={styles.actions}>
                          <button type="submit" className={styles.btn} disabled={saving}>
                            Save changes
                          </button>
                        </div>
                      </form>

                      <hr className={styles.divider} />

                      <h4 className={styles.subsectionTitle}>Enterprise administrators</h4>
                      <ul className={styles.adminList}>
                        {(detail.adminDirectory || []).map((a, idx) => (
                          <li key={`${a.email}-${a.status}-${idx}`}>
                            <span className={styles.adminName}>
                              {a.name || a.email}
                              <span className={styles.adminStatusBadge}>
                                {a.status === 'invited' ? 'Invited' : 'Active'}
                              </span>
                            </span>
                            <span className={styles.adminMeta}>
                              {a.email}
                              {a.status === 'active' && a.enterpriseUsersAddedCount != null
                                ? ` · ${a.enterpriseUsersAddedCount} users added`
                                : ''}
                              {a.status === 'invited' && a.inviteExpiresAt
                                ? ` · expires ${formatDate(a.inviteExpiresAt)}`
                                : ''}
                              {a.status === 'active' && a.joinedAt
                                ? ` · joined ${formatDate(a.joinedAt)}`
                                : ''}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <form
                        onSubmit={async (ev) => {
                          const emails = editForm.detailAdminEmails || [];
                          const ok = await handleInviteAdmins(ev, emails);
                          if (ok) setEditForm((f) => (f ? { ...f, detailAdminEmails: [] } : f));
                        }}
                        style={{ marginTop: 16 }}
                        data-no-enterprise-deselect
                      >
                        <div className={styles.field}>
                          <label htmlFor="detail-admins-chips">Add enterprise admins</label>
                          <AdminEmailChipField
                            inputId="detail-admins-chips"
                            emails={editForm.detailAdminEmails || []}
                            onChange={(next) =>
                              setEditForm((f) => (f ? { ...f, detailAdminEmails: next } : f))
                            }
                            disabled={saving}
                            hint="Same secure workflow as provisioning: existing accounts are activated immediately; new emails receive an invitation."
                            chipError={detailChipError}
                            setChipError={setDetailChipError}
                          />
                        </div>
                        <div className={styles.actions}>
                          <button
                            type="submit"
                            className={styles.btn}
                            disabled={saving || !(editForm.detailAdminEmails || []).length}
                          >
                            Assign admins
                          </button>
                        </div>
                      </form>

                      <hr className={styles.divider} />

                      <div className={styles.dangerZone} data-no-enterprise-deselect>
                        <h4 className={styles.dangerZoneTitle}>Danger zone</h4>
                        <p className={styles.dangerZoneLead}>
                          Permanently remove this enterprise from Velox. This does not delete user accounts — only this
                          company&apos;s enterprise workspace, teams, memberships, and invites.
                        </p>
                        <button
                          type="button"
                          className={styles.btnDangerOutline}
                          onClick={openDeleteModal}
                          disabled={saving || deleteSubmitting}
                        >
                          Delete enterprise account…
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}
            </aside>
          </div>

      {deleteModalOpen && enterpriseLegalName && selectedId
        ? createPortal(
            <div
              className={`vx-adminRoot ${styles.deleteModalPortalRoot}`}
              data-theme={theme}
              data-no-enterprise-deselect
            >
              <div
                className={styles.deleteModalOverlay}
                role="dialog"
                aria-modal="true"
                aria-labelledby="ent-delete-modal-title"
              >
                <div className={styles.deleteModal}>
                  <h3 id="ent-delete-modal-title" className={styles.deleteModalTitle}>
                    Delete enterprise account
                  </h3>
                  <p className={styles.deleteModalWarn}>
                    This will <strong>permanently remove</strong> <strong>{enterpriseLegalName}</strong> from Velox
                    Enterprise. This action cannot be undone.
                  </p>
                  <ul className={styles.deleteModalList}>
                    <li>All teams for this company</li>
                    <li>All enterprise memberships and roles for this company (admins and members)</li>
                    <li>All pending and historical enterprise invites for this company</li>
                    <li>Platform billing and notes stored on this enterprise record</li>
                  </ul>
                  <p className={styles.deleteModalPreserve}>
                    Velox <strong>user accounts</strong> are not deleted. Members simply lose access to this enterprise.
                    Enquiries that were provisioned into this company are unlinked and returned to{' '}
                    <strong>reviewed</strong> so they can be provisioned again if needed.
                  </p>
                  <label className={styles.deleteModalLabel} htmlFor="ent-delete-confirm-input">
                    Type the company name exactly to confirm
                  </label>
                  <input
                    id="ent-delete-confirm-input"
                    className={styles.deleteModalInput}
                    type="text"
                    value={deleteConfirmInput}
                    onChange={(ev) => setDeleteConfirmInput(ev.target.value)}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder={enterpriseLegalName}
                    disabled={deleteSubmitting}
                  />
                  <div className={styles.deleteModalActions}>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={closeDeleteModal}
                      disabled={deleteSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.btnDangerSolid}
                      onClick={executeDelete}
                      disabled={deleteSubmitting || !deleteNameMatches}
                    >
                      {deleteSubmitting ? 'Deleting…' : 'Permanently delete enterprise'}
                    </button>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
